// Con qué productos se generó una imagen.
//
// La respuesta vive en TRES lugares según cuándo y cómo se creó la fila, así que
// hay un solo lugar que los prueba en orden — ADR-2026-09-06-variantes-ids-columna-aditiva:
//
//   1. `variantes_ids`  — la columna nueva: la lista completa, principal primero.
//   2. `datos_generacion.variante_id` — array (o string suelto) del request. Es de
//      dónde salió el backfill; sigue como red por si una query no pide la columna.
//   3. `variante_id`    — la columna vieja: SOLO el principal.
//
// Devuelve ids de VARIANTE, que es lo que la UI de productos llama "producto"
// (`MockProductV2.id`). Para llegar al `productos.id` real hay que subir por
// `variantes.product_id`.

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Fila de `imagenes_generadas` vista por cualquiera de las pantallas. */
export interface FilaConVariantes {
  variantes_ids?: string[] | null;
  variante_id?: string | null;
  datos_generacion?: Record<string, unknown> | null;
}

function limpiar(valor: unknown): string | null {
  if (typeof valor !== "string") return null;
  const t = valor.trim().toLowerCase();
  return UUID.test(t) ? t : null;
}

function normalizar(ids: unknown[]): string[] {
  const vistos = new Set<string>();
  const out: string[] = [];
  for (const crudo of ids) {
    const id = limpiar(crudo);
    if (id === null || vistos.has(id)) continue;
    vistos.add(id);
    out.push(id);
  }
  return out;
}

/** Del jsonb del request: `variante_id` puede ser array o un string suelto. */
function desdeDatosGeneracion(dg: Record<string, unknown> | null | undefined): string[] {
  if (!dg) return [];
  const v = dg["variante_id"];
  if (Array.isArray(v)) return normalizar(v);
  return normalizar([v]);
}

/**
 * Todas las variantes con las que se generó la imagen, principal primero.
 * `[]` = no se generó con ninguna (asistente libre, creativo plus, shorts, foto
 * subida). No es "no sé": es "no hay".
 */
export function variantesDeImagen(fila: FilaConVariantes | null | undefined): string[] {
  if (!fila) return [];
  const columna = normalizar(fila.variantes_ids ?? []);
  if (columna.length > 0) return columna;
  const jsonb = desdeDatosGeneracion(fila.datos_generacion);
  if (jsonb.length > 0) {
    // El principal manda: si la columna vieja tiene valor, va primero.
    const principal = limpiar(fila.variante_id);
    return principal ? normalizar([principal, ...jsonb]) : jsonb;
  }
  return normalizar([fila.variante_id]);
}

/** Qué tiene que hacer la UI al mandar esta imagen a un lanzamiento. */
export type DestinoDeImagen =
  /** Un solo producto: va derecho, sin preguntar. */
  | { tipo: "directo"; productoId: string }
  /** Se generó con varios: el usuario elige entre ESOS. */
  | { tipo: "elegir-entre-estos"; productoIds: string[] }
  /** Sin producto: el usuario elige cualquiera de la marca. */
  | { tipo: "elegir-cualquiera" };

/**
 * Decide el flujo a partir de los PRODUCTOS (no de las variantes): dos variantes
 * del mismo producto son un solo destino y no deben abrir un selector.
 */
export function destinoDeImagen(productoIds: string[]): DestinoDeImagen {
  const unicos = normalizar(productoIds);
  if (unicos.length === 0) return { tipo: "elegir-cualquiera" };
  if (unicos.length === 1) return { tipo: "directo", productoId: unicos[0]! };
  return { tipo: "elegir-entre-estos", productoIds: unicos };
}
